import React from 'react'
import { useTranslation } from 'react-i18next'
import { getLastPathElement } from '../../util/StringUtil'
import { getPersonSummary } from '../../services/personService'

export default () => {}

export const RelationshipContext = React.createContext({})

export function RelationshipContextProvider({ value, children }) {
  return <RelationshipContext.Provider value={value}>{children}</RelationshipContext.Provider>
}

export function GetAssociationRelationshipRole(isPerson1, associationType) {
  const { t } = useTranslation()

  switch (associationType) {
    case 'SLAVEHOLDER_TO_ENSLAVED_PERSON':
      if (isPerson1) {
        return t('relationship.role.slaveholder', 'Slaveholder')
      }
      return t('relationship.role.enslaved', 'Enslaved Person')
    case 'ANCESTOR_TO_DESCENDANT':
      if (isPerson1) {
        return t('ancestor', 'Ancestor')
      }
      return t('relationship.role.descendant', 'Descendant')
    case 'HONORED_ANCESTOR_TO_DESCENDANT':
      if (isPerson1) {
        return t('relationship.role.honoredAncestor', 'Honored Ancestor')
      }
      return t('relationship.role.descendant', 'Descendant')
    case 'ASSOCIATE_TO_ASSOCIATE':
      return t('relationship.role.associate', 'Associate')
    case 'NEIGHBOR_TO_NEIGHBOR':
      return t('relationship.role.neighbor', 'Neighbor')
    case 'HEAD_OF_HOUSEHOLD_TO_OCCUPANT':
      if (isPerson1) {
        return t('relationship.role.head', 'Head of Household')
      }
      return t('relationship.role.occupant', 'Occupant')
    case 'GODPARENT_TO_GODCHILD':
      if (isPerson1) {
        return t('relationship.role.godparent', 'Godparent')
      }
      return t('relationship.role.godchild', 'Godchild')
    case 'EMPLOYER_TO_EMPLOYEE':
      if (isPerson1) {
        return t('relationship.role.employer', 'Employer')
      }
      return t('relationship.role.employee', 'Employee')
    case 'MASTER_TO_APPRENTICE':
      if (isPerson1) {
        return t('relationship.role.master', 'Master')
      }
      return t('relationship.role.apprentice', 'Apprentice')
    default:
      return t('relationship.role.associate', 'Associate')
  }
}

const mapEvent = (relationship, zionRelationship) => {
  const marriageEvent = relationship.facts?.find((fact) => {
    return getLastPathElement(fact.value?.type).toUpperCase() === 'MARRIAGE'
  })
  if (marriageEvent) {
    zionRelationship.event = {
      id: `${relationship.relationshipId}`,
      type: getLastPathElement(marriageEvent.value?.type).toUpperCase(),
      details: {},
    }

    if (marriageEvent.value.date) {
      zionRelationship.event.details.date = {
        localizedText: marriageEvent.value.date.normalized?.value,
        originalText: marriageEvent.value.date.original,
      }
    }
    if (marriageEvent.value.place) {
      zionRelationship.event.details.place = {
        localizedText: marriageEvent.value.place.names?.value,
        originalText: marriageEvent.value.place.original,
      }
    }
  }
}

const mapRelationshipChildren = (relationship, spouseId) => {
  const children = []

  if (relationship && relationship.children) {
    relationship.children.forEach((childrenOfOneParent) => {
      if (childrenOfOneParent.parent.personId === spouseId) {
        childrenOfOneParent.children.forEach((child) => {
          children.push({
            name: child.child.name,
            id: child.child.personId,
            gender: child.child.gender,
            lifespan: child.child.lifespan,
            nameConclusion: {
              type: 'NAME',
              details: {
                fullText: child.child.name,
              },
            },
          })
        })
      }
    })
  }

  return children
}

export function mapSpouseRelationshipToFamily(spouseRelationship, preferredSpouseId, person) {
  const personSelf = {
    name: person.name,
    id: person.id || person.personId,
    gender: person.gender,
    lifespan: person.lifespan,
    nameConclusion: {
      type: 'NAME',
      details: {
        fullText: person.name,
      },
    },
  }
  const spouse = {
    name: spouseRelationship.spouse?.name,
    id: spouseRelationship.spouse?.personId,
    gender: spouseRelationship.spouse?.gender,
    lifespan: spouseRelationship.spouse?.lifespan,
    nameConclusion: {
      type: 'NAME',
      details: {
        fullText: spouseRelationship.spouse?.name,
      },
    },
  }

  spouse.preferred = preferredSpouseId === spouseRelationship.spouse?.personId
  const children = mapRelationshipChildren(person.relationships, spouse.id)

  const relationship = {
    parent1: personSelf,
    parent2: spouse,
    children,
  }

  mapEvent(spouseRelationship, relationship)

  return relationship
}

export function mapParentRelationshipToFamily(parentRelationship) {
  // get father family:
  if (parentRelationship && parentRelationship.parent1 && parentRelationship.parent1.personId) {
    getPersonSummary(parentRelationship?.parent1?.personId)
      .then((fatherResults) => {
        if (fatherResults) {
          parentRelationship.children = fatherResults.relationships?.children
          // find spouse relationship

          if (parentRelationship && parentRelationship.parent2 && fatherResults.relationships.spouses) {
            const matchingSpouses = fatherResults.relationships.spouses.filter((spouseRelationship) => {
              return spouseRelationship.spouse.personId === parentRelationship?.parent2?.personId
            })
            parentRelationship.parent2 = matchingSpouses[0]?.spouse
            parentRelationship.facts = matchingSpouses[0]?.facts
          }
        }
      })
      .catch(() => {
        // Best-effort enrichment (extra children/spouse reconciliation for the family card) --
        // this util has no hook access to surface a notification, and the caller already
        // tolerates a missing `fatherResults` on success, so a failed fetch here just means
        // the family card renders without the extra detail instead of crashing the page.
      })
  }

  const parent1 = {
    name: parentRelationship.parent1?.name,
    id: parentRelationship.parent1?.personId,
    gender: parentRelationship.parent1?.gender,
    lifespan: parentRelationship.parent1?.lifespan,
    nameConclusion: {
      type: 'NAME',
      details: {
        fullText: parentRelationship.parent1?.name,
      },
    },
  }

  const parent2 = {
    name: parentRelationship.parent2?.name,
    id: parentRelationship.parent2?.personId,
    gender: parentRelationship.parent2?.gender,
    lifespan: parentRelationship.parent2?.lifespan,
    nameConclusion: {
      type: 'NAME',
      details: {
        fullText: parentRelationship.parent2?.name,
      },
    },
  }

  const children = mapRelationshipChildren(parentRelationship, parentRelationship.parent2?.personId)
  const relationship = {
    parent1,
    parent2,
    children,
  }

  mapEvent(parentRelationship, relationship)
  return relationship
}
